import React, { useState } from 'react';
import { Slide, Paper, Box, Typography, IconButton, Tooltip } from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import {
  Remove as MinimizeIcon,
  OpenInFull as RestoreIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

/**
 * Shared building blocks for "minimizable" dialogs — a Windows-style minimize
 * button that tucks an open dialog into a small bar at the bottom-right while
 * preserving everything the user typed (the dialog component stays mounted).
 *
 * Usage in a form dialog:
 *   const { minimized, minimize, restore, setMinimized, dialogOpen } =
 *     useMinimizable(open, controlledMinimized, onMinimizedChange);
 *   ...
 *   <Dialog open={dialogOpen} TransitionComponent={SlideUpTransition} ...>
 *     <DialogTitle> ... <MinimizeButton color="white" onClick={minimize} /> ...
 *   </Dialog>
 *   <MinimizedBar open={open && minimized} title="..." color={MODULE_COLOR}
 *     onRestore={restore} onClose={handleClose} />
 *
 * The parent should reset minimize when re-opening (Add/Edit) so the trigger
 * restores a minimized dialog — pass `minimized` + `onMinimizedChange`.
 */

// Dialog rises from the bottom on open and drops back down on close/minimize.
export const SlideUpTransition = React.forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Minimize state, controlled by the parent when provided, else internal.
 * Returns `dialogOpen` = `open && !minimized` for the <Dialog open> prop.
 */
export function useMinimizable(
  open: boolean,
  controlledMinimized?: boolean,
  onMinimizedChange?: (minimized: boolean) => void
) {
  const [internalMinimized, setInternalMinimized] = useState(false);
  const minimized = controlledMinimized ?? internalMinimized;
  const setMinimized = (value: boolean) => {
    if (onMinimizedChange) onMinimizedChange(value);
    else setInternalMinimized(value);
  };
  return {
    minimized,
    setMinimized,
    minimize: () => setMinimized(true),
    restore: () => setMinimized(false),
    dialogOpen: open && !minimized,
  };
}

export const MinimizeButton: React.FC<{
  onClick: () => void;
  color?: string;
}> = ({ onClick, color = 'inherit' }) => (
  <Tooltip title="Réduire">
    <IconButton size="small" onClick={onClick} sx={{ color }}>
      <MinimizeIcon />
    </IconButton>
  </Tooltip>
);

/**
 * Title-bar window controls: a minimize (–) button followed by a close (✕)
 * button. Use this in dialogs whose title has no close icon of its own so they
 * get both controls in one consistent group.
 */
export const DialogControls: React.FC<{
  onMinimize: () => void;
  onClose: () => void;
  color?: string;
}> = ({ onMinimize, onClose, color = 'inherit' }) => (
  <Box display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
    <Tooltip title="Réduire">
      <IconButton size="small" onClick={onMinimize} sx={{ color }}>
        <MinimizeIcon />
      </IconButton>
    </Tooltip>
    <Tooltip title="Fermer">
      <IconButton size="small" onClick={onClose} sx={{ color }}>
        <CloseIcon />
      </IconButton>
    </Tooltip>
  </Box>
);

export const MinimizedBar: React.FC<{
  open: boolean;
  title: string;
  color?: string;
  onRestore: () => void;
  onClose: () => void;
}> = ({ open, title, color = '#1976D2', onRestore, onClose }) => (
  <Slide direction="up" in={open} mountOnEnter unmountOnExit>
    <Paper
      elevation={8}
      onClick={onRestore}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: (theme) => theme.zIndex.modal + 1,
        width: 340,
        maxWidth: 'calc(100vw - 32px)',
        bgcolor: color,
        color: 'white',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.5,
        py: 1,
        cursor: 'pointer',
      }}
    >
      <Typography variant="body2" noWrap sx={{ fontWeight: 600, mr: 1 }}>
        {title}
      </Typography>
      <Box display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
        <Tooltip title="Restaurer">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRestore();
            }}
            sx={{ color: 'white' }}
          >
            <RestoreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fermer">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            sx={{ color: 'white' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  </Slide>
);
